import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, User, LogOut } from 'lucide-react';

const API = '/api';

export default function PlayDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = React.useContext(AuthContext);
  const [play, setPlay] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlayDetail();
  }, [id]);

  const fetchPlayDetail = async () => {
    try {
      const response = await axios.get(`${API}/plays/${id}`);
      setPlay(response.data);
    } catch (error) {
      console.error('Error fetching play:', error);
      if (error.response?.status === 403 && error.response?.data?.detail) {
        // Quota error
        toast.error(error.response.data.detail, { duration: 5000 });
      } else {
        toast.error('Oyun detayları yüklenemedi');
      }
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Başarıyla çıkış yapıldı');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="curtain-bg min-h-screen flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!play) {
    return null;
  }

  return (
    <div className="curtain-bg min-h-screen">
      {/* Navbar */}
      <nav className="navbar fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
            <img src="/onk-logo.png" alt="ONK Ajans" style={{ width: '175px', height: 'auto' }} />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-900/20 rounded-lg border border-amber-700/30">
              <User className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-gray-300">{user?.email}</span>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-400 hover:text-amber-400"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-48 px-6 pb-12">
        <div className="max-w-4xl mx-auto">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-amber-400 hover:text-amber-300 mb-6"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kontrol Paneline Dön
          </Button>

          <div className="glass-effect rounded-xl p-8">
            <h1 
              className="text-4xl sm:text-5xl font-bold gold-glow mb-4"
              data-testid="play-detail-title"
            >
              {play.title}
            </h1>
            <p className="text-2xl text-amber-400 mb-6" data-testid="play-detail-playwright">
              yazan: {play.playwright}
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8 p-4 bg-black/30 rounded-lg">
              <div data-testid="play-detail-genre">
                <p className="text-amber-500 font-semibold text-sm mb-1">Tür</p>
                <p className="text-gray-200">{play.genre}</p>
              </div>
              <div data-testid="play-detail-male">
                <p className="text-amber-500 font-semibold text-sm mb-1">Erkek Karakterler</p>
                <p className="text-gray-200">{play.male_characters}</p>
              </div>
              <div data-testid="play-detail-female">
                <p className="text-amber-500 font-semibold text-sm mb-1">Kadın Karakterler</p>
                <p className="text-gray-200">{play.female_characters}</p>
              </div>
            </div>

            <div className="mb-6">
              <h2 
                className="text-2xl font-bold mb-4 text-amber-400"
              >
                Oyun Metni
              </h2>
              {play.pdf_path ? (
                <div className="bg-black/30 p-4 rounded-lg">
                  <iframe
                    src={`${API}/plays/${play.id}/pdf`}
                    className="w-full h-[800px] rounded"
                    title="Oyun Metni PDF"
                    data-testid="play-detail-pdf"
                  />
                  <div className="mt-4 text-center">
                    <a
                      href={`${API}/plays/${play.id}/pdf`}
                      download={`${play.title}.pdf`}
                      className="inline-block px-6 py-3 bg-[#c50605] hover:bg-[#9d0504] text-white rounded"
                    >
                      PDF'i İndir
                    </a>
                  </div>
                </div>
              ) : (
                <div 
                  className="prose prose-invert max-w-none bg-black/30 p-6 rounded-lg text-gray-200 whitespace-pre-wrap leading-relaxed"
                  data-testid="play-detail-text"
                >
                  {play.text || 'Oyun metni yüklenmemiş.'}
                </div>
              )}
            </div>

            <div className="text-sm text-gray-500 border-t border-amber-900/30 pt-4">
              <p>Yükleyen: {play.uploaded_by}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}